import { config } from '@/lib/config';
import { useRtmStore } from '@/lib/store/rtm';
import { useQuery } from '@tanstack/react-query';
/**
 * TanStack Query hooks for the Grafana-backed RTM Services view
 * (ClickUp 86e1w8ude, phase 1).
 */
import { useEffect } from 'react';
import { grafanaLokiReady, grafanaPromReady } from './client';
import { fetchHostingSeries, fetchRtmEnvelope, fetchServiceLogs, fetchServiceSeries } from './rtm';

/**
 * Polls Prometheus for the RTM services + hosting roll-up and pushes each
 * snapshot into the RTM store (the same store the MQTT mock writes). Mount
 * once where the RTM view lives. Disabled in mock mode and when Grafana
 * config is absent, so the mock data plane is untouched.
 */
export function useRtmGrafanaSync() {
  const apply = useRtmStore((s) => s.apply);
  const enabled = !config.isMock && grafanaPromReady();

  const query = useQuery({
    queryKey: ['grafana', 'rtm', 'envelope'],
    queryFn: fetchRtmEnvelope,
    enabled,
    refetchInterval: config.grafana.pollMs,
    refetchIntervalInBackground: false,
    staleTime: config.grafana.pollMs,
  });

  useEffect(() => {
    if (query.data) apply(query.data);
  }, [query.data, apply]);

  return { enabled, ...query };
}

/** Recent Loki log lines for the selected service (pod), newest first. */
export function useServiceLogs(pod: string | null, windowKey: string) {
  const enabled = !config.isMock && grafanaLokiReady() && !!pod;
  return useQuery({
    queryKey: ['grafana', 'loki', pod, windowKey],
    queryFn: () => fetchServiceLogs(pod!, windowKey, 100),
    enabled,
    refetchInterval: 10_000,
    refetchIntervalInBackground: false,
    staleTime: 5_000,
  });
}

/** Range time-series for the selected service's detail charts. */
export function useServiceSeries(pod: string | null, windowKey: string) {
  const enabled = !config.isMock && grafanaPromReady() && !!pod;
  return useQuery({
    queryKey: ['grafana', 'svc-series', pod, windowKey],
    queryFn: () => fetchServiceSeries(pod!, windowKey),
    enabled,
    refetchInterval: config.grafana.pollMs,
    refetchIntervalInBackground: false,
    staleTime: config.grafana.pollMs,
  });
}

/** Range time-series for the Hosting Environment mini charts + sparklines. */
export function useHostingSeries(windowKey: string) {
  const enabled = !config.isMock && grafanaPromReady();
  return useQuery({
    queryKey: ['grafana', 'hosting-series', windowKey],
    queryFn: () => fetchHostingSeries(windowKey),
    enabled,
    refetchInterval: config.grafana.pollMs,
    refetchIntervalInBackground: false,
    staleTime: config.grafana.pollMs,
  });
}
